import type { WriteRejection } from "@estelle/domain";
import { Loader2, RotateCcw } from "lucide-react";

import { summarize, type BulkOutcome } from "@/components/table/bulk";
import { Button } from "@/components/ui/button";

/**
 * What a bulk run came to (#634, FR-CAT-8): how many went through, which did
 * not and why, and a retry of only the failed ones — "3 of 12 failed" with no
 * names is useless to the person who has to fix them.
 */
export function BulkReport<T>({
  outcomes,
  labelOf,
  describe = (rejection) => rejection.message,
  onRetry,
  retrying,
  verb = "applied",
}: {
  outcomes: BulkOutcome<T>[];
  labelOf: (item: T) => string;
  describe?: (rejection: WriteRejection) => string;
  onRetry: () => void;
  retrying: boolean;
  /** "12 of 40 confirmed". */
  verb?: string;
}) {
  const summary = summarize(outcomes);
  const failed = outcomes.filter((outcome) => outcome.status !== "success");

  return (
    <div className="space-y-1.5 text-sm">
      <p>
        {summary.succeeded} of {summary.total} {verb}
        {summary.failed > 0
          ? `, ${String(summary.failed)} refused (nothing changed on those):`
          : "."}
      </p>
      {failed.length > 0 ? (
        <>
          <ul className="max-h-40 space-y-1 overflow-auto">
            {/* Rendered once per result and never reordered, so position is
                a stable enough key for items that carry no id of their own. */}
            {failed.map((outcome, index) => (
              <li key={index} className="text-destructive text-xs">
                <span className="font-medium">{labelOf(outcome.item)}:</span>{" "}
                {outcome.status === "rejected"
                  ? describe(outcome.rejection)
                  : outcome.status === "error"
                    ? outcome.message
                    : null}
              </li>
            ))}
          </ul>
          <Button
            type="button"
            size="xs"
            variant="outline"
            disabled={retrying}
            onClick={onRetry}
          >
            {retrying ? <Loader2 className="animate-spin" /> : <RotateCcw />}
            Retry the {summary.failed} failed
          </Button>
        </>
      ) : null}
    </div>
  );
}

/** "12 of 40" while a bulk run is in flight. */
export function BulkProgress({
  progress,
}: {
  progress: { done: number; total: number };
}) {
  return (
    <p
      role="status"
      className="text-muted-foreground flex items-center gap-1.5 text-sm"
    >
      <Loader2 className="size-3.5 animate-spin" aria-hidden />
      {progress.done} of {progress.total}…
    </p>
  );
}
